<template>
  <div class="dom-page py-3">
    <header class="dom-toolbar">
      <div class="dom-toolbar__title">
        <span class="dom-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Domains</h1>
          <p>Namecheap-managed domains and DNS records. Super-admin only.</p>
        </div>
      </div>
      <div class="dom-toolbar__actions">
        <CButton
          size="sm"
          class="refresh-btn"
          :class="{ 'is-loading': isLoading }"
          :disabled="isLoading"
          @click="loadDomains(true)"
        >
          {{ isLoading ? 'Refreshing…' : 'Refresh' }}
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="dom-alert">{{ errorMessage }}</CAlert>

    <section class="dom-section">
      <div class="dom-section__body">
        <div class="dom-filters">
          <div class="dom-filter">
            <label class="dom-label">Search</label>
            <AppInput
              no-margin
              :value="search"
              placeholder="Filter by domain name…"
              @update:value="search = $event"
            />
          </div>
          <div class="dom-filter dom-filter--hint">
            <label class="dom-label" aria-hidden="true">&nbsp;</label>
            <small class="dom-hint">{{ paging.total_items ? `${paging.total_items} domains on this Namecheap account` : 'No domains loaded' }}</small>
          </div>
        </div>
      </div>
    </section>

    <BaseTable
      title="Domains"
      :items="tableRows"
      :columns="columns"
      :loading="isLoading"
      empty-text="No domains found. Check Namecheap API credentials and IP whitelist."
    >
      <template #cell-name="{ row }">
        <strong>{{ row.name }}</strong>
      </template>
      <template #cell-expires="{ row }">
        <span :class="['dom-pill', expiryPillClass(row.daysToExpiry)]">
          {{ row.expiresLabel }}
        </span>
      </template>
      <template #cell-auto_renew="{ row }">
        <span :class="['dom-pill', row.auto_renew ? 'dom-pill--green' : 'dom-pill--gray']">
          {{ row.auto_renew ? 'On' : 'Off' }}
        </span>
      </template>
      <template #cell-whois_guard="{ row }">
        <span class="dom-mini">{{ row.whois_guard || '—' }}</span>
      </template>
      <template #cell-actions="{ row }">
        <div class="dom-row-actions">
          <button type="button" class="dom-action-btn dom-action-btn--edit" @click="openDns(row)">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10"/>
              <path d="M2 12h20"/>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
            </svg>
            <span>DNS</span>
          </button>
        </div>
      </template>
    </BaseTable>
  </div>
</template>

<script>
import AppInput from '@/components/AppInput.vue'
import BaseTable from '@/components/BaseTable.vue'
import { domainsApi } from '@/api/domains.js'

export default {
  name: 'SettingsDomains',
  components: { AppInput, BaseTable },
  data () {
    return {
      domains: [],
      paging: {},
      isLoading: false,
      errorMessage: '',
      search: '',
      columns: [
        { key: 'name', label: 'Domain' },
        { key: 'expires', label: 'Expires', width: '180px' },
        { key: 'auto_renew', label: 'Auto-Renew', width: '120px' },
        { key: 'whois_guard', label: 'WhoisGuard', width: '160px' },
        { key: 'actions', label: 'Actions', width: '110px', align: 'right', sortable: false }
      ]
    }
  },
  computed: {
    tableRows () {
      const today = new Date()
      const term = this.search.trim().toLowerCase()
      return this.domains
        .filter((d) => !term || d.name.toLowerCase().includes(term))
        .map((d) => {
          const expiresDate = this.parseNamecheapDate(d.expires)
          const daysToExpiry = expiresDate
            ? Math.round((expiresDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
            : null
          let expiresLabel = d.expires || '—'
          if (daysToExpiry !== null) {
            if (daysToExpiry < 0) expiresLabel = `Expired (${d.expires})`
            else if (daysToExpiry === 0) expiresLabel = `Today (${d.expires})`
            else expiresLabel = `${d.expires} · ${daysToExpiry}d`
          }
          return { ...d, expiresLabel, daysToExpiry }
        })
    }
  },
  created () {
    this.loadDomains(false)
  },
  methods: {
    loadDomains (forceRefresh) {
      this.isLoading = true
      this.errorMessage = ''
      const params = forceRefresh ? { refresh: 1 } : undefined
      domainsApi.list(params)
        .then((response) => {
          this.domains = Array.isArray(response.domains) ? response.domains : []
          this.paging = response.paging || {}
        })
        .catch((error) => {
          this.errorMessage = error?.response?.data?.message || error.message || 'Unable to load domains.'
        })
        .finally(() => { this.isLoading = false })
    },
    parseNamecheapDate (str) {
      // Namecheap returns dates as MM/DD/YYYY in the getList response.
      if (!str || typeof str !== 'string') return null
      const parts = str.split('/')
      if (parts.length !== 3) return null
      const [m, d, y] = parts.map((p) => parseInt(p, 10))
      if (!m || !d || !y) return null
      return new Date(y, m - 1, d)
    },
    expiryPillClass (days) {
      if (days === null || days === undefined) return 'dom-pill--gray'
      if (days < 0) return 'dom-pill--red'
      if (days <= 30) return 'dom-pill--amber'
      return 'dom-pill--green'
    },
    openDns (row) {
      this.$router.push({ name: 'SettingsDomainDns', params: { domain: row.name } })
    }
  }
}
</script>

<style scoped>
.dom-page { padding: 0.25rem 0; }

.dom-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
  flex-wrap: wrap;
}

.dom-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.dom-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.dom-toolbar h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.dom-toolbar p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.dom-toolbar__actions { display: flex; gap: 0.5rem; flex-wrap: wrap; }

.dom-toolbar__actions >>> .btn {
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
}

.dom-toolbar__actions >>> .btn.refresh-btn {
  height: 34px;
  padding: 0 0.85rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
}

.dom-toolbar__actions >>> .btn.refresh-btn:focus { outline: none; }
.dom-toolbar__actions >>> .btn.refresh-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.dom-alert { margin: 0 0 0.75rem; border-radius: 10px; }

.dom-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

.dom-section__body { padding: 1rem; }

.dom-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 0.85rem;
  align-items: flex-end;
}

.dom-filter {
  display: flex;
  flex-direction: column;
  flex: 1 1 240px;
  min-width: 0;
}

.dom-filter--hint { flex: 1 1 280px; }

.dom-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.dom-hint {
  font-size: 0.78rem;
  color: #6b7280;
  line-height: 36px;
}

.dom-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.12rem 0.55rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.dom-pill--green { background: #dff5ea; color: #0e5b46; }
.dom-pill--amber { background: #fef3c7; color: #92400e; }
.dom-pill--red   { background: #fee2e2; color: #991b1b; }
.dom-pill--gray  { background: #f1f5f9; color: #475569; }

.dom-mini { font-size: 0.78rem; color: #4b5563; }

.dom-row-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  justify-content: flex-end;
}

.dom-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  height: 28px;
  padding: 0 0.7rem;
  border-radius: 6px;
  border: 1px solid #e5e9f2;
  background: #ffffff;
  color: #1f2a44;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.dom-action-btn:focus { outline: none; }
.dom-action-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.dom-action-btn--edit:hover {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 4px rgba(14, 91, 70, 0.1);
}
</style>
